import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useApp } from '../../context/AppContext'
import UserAvatar from '../Shared/UserAvatar'
import TicketTypeBadge from '../ticket/TicketTypeBadge'
import useMyTickets from '../../hooks/useMyTickets'
import useTicketCatalogs from '../../hooks/useTicketCatalogs'
import { employeeName } from '../../utils/employee'
import { greeting, summarizeMyTickets } from '../../utils/myDashboard'

/** Accesos rápidos para quien reporta. */
const QUICK_ACTIONS = [
  {
    to: '/nuevo-ticket',
    icon: 'fa-plus',
    label: 'Nuevo ticket',
    hint: 'Reportar una falla o pedir algo',
    color: 'var(--brand)',
  },
  {
    to: '/reportar-incidente',
    icon: 'fa-shield-halved',
    label: 'Incidente de seguridad',
    hint: 'Phishing, robo de equipo, fuga de datos',
    color: 'var(--gm-danger)',
  },
  {
    to: '/mis-tickets',
    icon: 'fa-list-ul',
    label: 'Mis tickets',
    hint: 'Ver el avance de mis reportes',
    color: 'var(--accent)',
  },
  {
    to: '/ayuda',
    icon: 'fa-circle-question',
    label: 'Centro de ayuda',
    hint: 'Guías y preguntas frecuentes',
    color: 'var(--gm-info)',
  },
]

const RECENT_LIMIT = 6

/**
 * Dashboard personal para quien no es de soporte: saludo, accesos rápidos,
 * resumen de SUS tickets y los más recientes. No consulta datos de otras
 * personas (eso queda en el dashboard operativo de soporte).
 *
 * @component
 * @returns {JSX.Element}
 */
const MyDashboard = () => {
  const navigate = useNavigate()
  const { employee } = useApp()
  const { tickets, loading } = useMyTickets()
  const { statusById, employeeById } = useTicketCatalogs()

  const summary = useMemo(() => summarizeMyTickets(tickets), [tickets])
  const recent = tickets.slice(0, RECENT_LIMIT)
  const firstName = employee?.nombre?.split(' ')[0] || ''

  const stats = [
    {
      label: 'Abiertos',
      value: summary.open,
      color: 'var(--accent)',
      icon: 'fa-folder-open',
    },
    {
      label: 'Resueltos',
      value: summary.resolved,
      color: 'var(--gm-success)',
      icon: 'fa-circle-check',
    },
    {
      label: 'Esperando asignación',
      value: summary.unassigned,
      color: 'var(--text-2)',
      icon: 'fa-user-clock',
    },
    {
      label: 'Total reportados',
      value: summary.total,
      color: 'var(--brand)',
      icon: 'fa-ticket',
    },
  ]

  return (
    <div className="md">
      <header className="md-hero">
        <div>
          <h4 className="md-hello">
            {greeting()}
            {firstName && `, ${firstName}`}
          </h4>
          <p className="md-sub">¿En qué le podemos ayudar hoy?</p>
        </div>
      </header>

      <nav className="md-actions" aria-label="Accesos rápidos">
        {QUICK_ACTIONS.map((a) => (
          <Link
            key={a.to}
            to={a.to}
            className="md-action"
            style={{ '--a-color': a.color }}
          >
            <span className="md-action-icon">
              <i className={`fa-solid ${a.icon}`} />
            </span>
            <span className="md-action-text">
              <span className="md-action-label">{a.label}</span>
              <span className="md-action-hint">{a.hint}</span>
            </span>
          </Link>
        ))}
      </nav>

      <section className="md-stats" aria-label="Resumen de mis tickets">
        {stats.map((s) => (
          <div key={s.label} className="md-stat">
            <i className={`fa-solid ${s.icon}`} style={{ color: s.color }} />
            <span className="md-stat-value">{loading ? '—' : s.value}</span>
            <span className="md-stat-label">{s.label}</span>
          </div>
        ))}
      </section>

      <section className="md-card">
        <div className="md-card-head">
          <span className="md-card-title">Mis tickets recientes</span>
          {tickets.length > RECENT_LIMIT && (
            <Link to="/mis-tickets" className="md-link">
              Ver todos ({tickets.length})
            </Link>
          )}
        </div>

        {loading ? (
          <div className="md-empty">
            <span className="spinner-border spinner-border-sm text-primary" />
          </div>
        ) : recent.length === 0 ? (
          <div className="md-empty">
            <i className="fa-regular fa-folder-open" />
            <span>Aún no ha reportado ningún ticket.</span>
            <Link to="/nuevo-ticket" className="btn btn-sm btn-primary mt-1">
              Crear mi primer ticket
            </Link>
          </div>
        ) : (
          <ul className="md-list">
            {recent.map((t) => {
              const status = statusById[t.statusId]
              const responsible = employeeById[t.responsibleId]
              const color = t.completed ? 'var(--gm-success)' : status?.color || 'var(--text-2)'
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    className="md-item"
                    onClick={() => navigate(`/ticket/${t.id}`)}
                  >
                    <span className="md-item-main">
                      <span className="md-item-title">{t.title}</span>
                      <span className="md-item-meta">
                        #{t.id} ·{' '}
                        {new Date(t.createdAt).toLocaleDateString('es-CR', {
                          day: 'numeric',
                          month: 'short',
                        })}
                        <TicketTypeBadge type={t.type} sm />
                      </span>
                    </span>
                    <span className="md-item-who">
                      {responsible ? (
                        <>
                          <UserAvatar employee={responsible} size={20} />
                          <span className="d-none d-md-inline">
                            {employeeName(responsible)}
                          </span>
                        </>
                      ) : (
                        <span className="md-muted">Sin asignar</span>
                      )}
                    </span>
                    <span
                      className="md-status"
                      style={{ color, background: `${color}1a` }}
                    >
                      {t.completed ? 'Resuelto' : status?.name || 'En proceso'}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <style>{`
        .md { display: flex; flex-direction: column; gap: 14px; max-width: 1100px; margin: 0 auto; padding: 4px 0 16px; }
        .md-hello { margin: 0; font-weight: 700; color: var(--gm-text); }
        .md-sub { margin: 2px 0 0; font-size: 0.9rem; color: var(--gm-text-muted); }

        .md-actions { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; }
        .md-action {
          display: flex; align-items: center; gap: 12px; padding: 14px; text-decoration: none;
          border: 1px solid var(--gm-border); border-radius: 12px; background: var(--gm-surface);
          transition: border-color 0.15s ease, transform 0.15s ease;
        }
        .md-action:hover { border-color: var(--a-color); transform: translateY(-2px); }
        .md-action-icon {
          flex-shrink: 0; width: 40px; height: 40px; border-radius: 12px; display: flex;
          align-items: center; justify-content: center; font-size: 1rem;
          color: var(--a-color); background: color-mix(in srgb, var(--a-color) 14%, transparent);
        }
        .md-action-text { display: flex; flex-direction: column; min-width: 0; }
        .md-action-label { font-weight: 600; font-size: 0.9rem; color: var(--gm-text); }
        .md-action-hint { font-size: 0.75rem; color: var(--gm-text-muted); }

        .md-stats {
          display: flex; flex-wrap: wrap; border: 1px solid var(--gm-border);
          border-radius: 12px; background: var(--gm-surface);
        }
        .md-stat { flex: 1 1 160px; display: flex; align-items: center; gap: 8px; padding: 12px 16px; }
        .md-stat + .md-stat { border-left: 1px solid var(--gm-border); }
        .md-stat i { font-size: 0.85rem; }
        .md-stat-value { font-size: 1.2rem; font-weight: 700; color: var(--gm-text); font-variant-numeric: tabular-nums; }
        .md-stat-label { font-size: 0.78rem; color: var(--gm-text-muted); }

        .md-card { border: 1px solid var(--gm-border); border-radius: 12px; background: var(--gm-surface); }
        .md-card-head { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; border-bottom: 1px solid var(--gm-border); }
        .md-card-title { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--gm-text-muted); }
        .md-link { font-size: 0.8rem; font-weight: 600; color: var(--gm-primary); text-decoration: none; }
        .md-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 28px 12px; font-size: 0.85rem; color: var(--gm-text-faint); }
        .md-empty i { font-size: 1.4rem; opacity: 0.6; }
        .md-list { list-style: none; margin: 0; padding: 4px; }
        .md-item {
          display: flex; align-items: center; gap: 12px; width: 100%; padding: 9px 10px; text-align: left;
          border: none; border-radius: 8px; background: transparent;
        }
        .md-item:hover { background: var(--gm-surface-2); }
        .md-item-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
        .md-item-title { font-weight: 600; font-size: 0.86rem; color: var(--gm-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .md-item-meta { display: flex; align-items: center; gap: 6px; font-size: 0.72rem; color: var(--gm-text-faint); }
        .md-item-who { display: inline-flex; align-items: center; gap: 6px; font-size: 0.78rem; color: var(--gm-text-muted); flex-shrink: 0; }
        .md-muted { font-style: italic; color: var(--gm-text-faint); }
        .md-status { flex-shrink: 0; padding: 2px 9px; border-radius: 99px; font-size: 0.7rem; font-weight: 600; white-space: nowrap; }
        @media (max-width: 575px) {
          .md-stat + .md-stat { border-left: none; }
        }
      `}</style>
    </div>
  )
}

export default MyDashboard
